import Bugsnag from "@bugsnag/js";
import { client } from "@nju/http-client";
import { BasicError, err } from "@nju/result";
import {
  ICreateChangeMsisdnOrderResult,
  ICreateFtthOrderResult,
  ICreateMultiOrderMsisdnOrderResult,
  ICreateOrderResult,
} from "./types";
import {
  changeMsisdnOrchestrator,
  ftthOrchestrator,
  multiOrderOrchestrator,
  verificationErrorResponse,
} from "./validators";

interface ICreateFtthOrder {
  orchestratorId: string;
  offerIds: string[];
  installationToken?: string;
  mode: "ftth";
}

interface ICreateChangeMsisdnOrder {
  msisdnToken?: string;
  orchestratorId: string;
  mode: "changeMsisdn";
}

interface ICreateMultiOrderMsisdnOrder {
  msisdnToken?: string;
  orchestratorId: string;
  mode: "mobile";
  processId: string;
  otpToken?: string;
}

const modeToOrchestratorMap = {
  ftth: ftthOrchestrator,
  mobile: multiOrderOrchestrator,
  changeMsisdn: changeMsisdnOrchestrator,
} as const;

function getParams(
  order:
    | ICreateFtthOrder
    | ICreateChangeMsisdnOrder
    | ICreateMultiOrderMsisdnOrder,
) {
  if (order.mode === "ftth") {
    return {
      offerIds: order.offerIds,
      installationToken: order.installationToken,
      supportsConsentsStep: true,
    };
  }

  if (order.mode === "changeMsisdn") {
    return {
      msisdnToken: order.msisdnToken,
    };
  }

  if (order.mode === "mobile") {
    return {
      processId: order.processId,
      msisdnToken: order.msisdnToken,
      otpToken: order.otpToken,
    };
  }

  throw new Error("invalid mode");
}

export async function createOrder(
  order:
    | ICreateFtthOrder
    | ICreateChangeMsisdnOrder
    | ICreateMultiOrderMsisdnOrder,
): Promise<ICreateOrderResult> {
  const serverResponse = modeToOrchestratorMap[order.mode];

  const result = await client(
    `${process.env.NEXT_PUBLIC_PUBLIC_GATEWAY_HOST}/orchestrators/${order.orchestratorId}/actions/ORDER_CREATE`,
    {
      method: "post",
      validator: serverResponse,
      json: {
        ...getParams(order),
      },
      headers: {
        version: "2",
        apikey: process.env.NEXT_PUBLIC_APIKEY || "unknown",
      },
    },
  );

  if (result.isError) {
    const errorInfo = verificationErrorResponse.safeParse(
      result.err.extra?.json,
    );

    if (errorInfo.success) {
      if (errorInfo.data.code === "50193") {
        return err(
          new BasicError({
            message: "User orchestrator is locked by other sales-agent",
            code: "ORCHESTRATORS_SALES_AGENT_LOCK_EXCEPTION",
          }),
        );
      }

      if (errorInfo.data.code === "50196") {
        return err(
          new BasicError({
            message: "another order is already in progress",
            code: "ANOTHER_ORDER_IN_PROGRESS",
          }),
        );
      }

      if (errorInfo.data.code === "50198") {
        return err(
          new BasicError({
            message: "Unsupported transfer from FLEX to NJU",
            code: "TRANSFER_ERROR_FLEX_TO_NJU",
          }),
        );
      }

      if (errorInfo.data.code === "50207") {
        return err(
          new BasicError({
            message: "Unsupported transfer from NJU to NJU",
            code: "TRANSFER_ERROR_NJU_TO_NJU",
          }),
        );
      }

      if (errorInfo.data.code === "50210" || errorInfo.data.code === "50232") {
        return err(
          new BasicError({
            message:
              "Cannot perform MNP migration while technical verification is in progress",
            code: "MNP_IN_CLIENT_VERIFICATION_IN_PROGRESS",
          }),
        );
      }

      if (errorInfo.data.code === "50138") {
        return err(
          new BasicError({
            message: "CANCEL_ORDER is already pending",
            code: "ORDER_CANCEL_IN_PENDING_CANCELLATION",
          }),
        );
      }
    }

    Bugsnag.notify(result.err);
    return result;
  }

  return result;
}

export function createFtthOrder(order: ICreateFtthOrder) {
  return createOrder(order) as Promise<ICreateFtthOrderResult>;
}

export function createChangeMsisdnOrder(order: ICreateChangeMsisdnOrder) {
  return createOrder(order) as Promise<ICreateChangeMsisdnOrderResult>;
}

export function createMultiOrderMsisdnOrder(
  order: ICreateMultiOrderMsisdnOrder,
) {
  return createOrder(order) as Promise<ICreateMultiOrderMsisdnOrderResult>;
}
